export type ActivityLocale = 'en' | 'ar';

export interface ActivityEventInput {
  action?: unknown;
  module?: unknown;
  targetName?: unknown;
  metadata?: Record<string, unknown> | null;
}

export interface ActivityChange {
  field: string;
  label: string;
  before?: string;
  after?: string;
}

export interface TranslatedActivityEvent {
  action: string;
  actionLabel: string;
  title: string;
  summary?: string;
  category: 'create' | 'approve' | 'payment' | 'edit' | 'remove' | 'add' | 'security' | 'unknown';
  changes: ActivityChange[];
  facts: Array<{ label: string; value: string }>;
  isKnown: boolean;
}

const HIDDEN_FIELDS = new Set([
  'updated_at', 'updatedAt', 'created_at', 'createdAt', 'org_id', 'orgId',
  'metadata', 'raw', 'payload', 'request', 'response',
]);

const COPY = {
  en: {
    create: 'Created', approve: 'Approved', payment: 'Payment recorded', paymentReversed: 'Payment reversed',
    edit: 'Updated', remove: 'Removed', add: 'Added', security: 'Security event', activity: 'Activity recorded',
    document: 'document', changed: 'Changed', from: 'from', to: 'to', none: 'None', yes: 'Yes', no: 'No',
    amount: 'Amount', account: 'Account', reference: 'Reference', method: 'Method', paymentDate: 'Payment date',
    paymentType: 'Payment type', contact: 'Contact', status: 'Status', details: 'Details', reason: 'Reason',
  },
  ar: {
    create: 'تم الإنشاء', approve: 'تم الاعتماد', payment: 'تم تسجيل دفعة', paymentReversed: 'تم عكس الدفعة',
    edit: 'تم التحديث', remove: 'تمت الإزالة', add: 'تمت الإضافة', security: 'حدث أمني', activity: 'تم تسجيل نشاط',
    document: 'المستند', changed: 'تم تغيير', from: 'من', to: 'إلى', none: 'لا يوجد', yes: 'نعم', no: 'لا',
    amount: 'المبلغ', account: 'الحساب', reference: 'المرجع', method: 'الطريقة', paymentDate: 'تاريخ الدفع',
    paymentType: 'نوع الدفع', contact: 'جهة الاتصال', status: 'الحالة', details: 'التفاصيل', reason: 'السبب',
  },
} as const;

const FIELD_LABELS: Record<string, { en: string; ar: string }> = {
  status: { en: 'Status', ar: 'الحالة' },
  contactId: { en: 'Contact', ar: 'جهة الاتصال' },
  contact_id: { en: 'Contact', ar: 'جهة الاتصال' },
  contactName: { en: 'Contact', ar: 'جهة الاتصال' },
  totalAmount: { en: 'Total amount', ar: 'المبلغ الإجمالي' },
  total_amount: { en: 'Total amount', ar: 'المبلغ الإجمالي' },
  dueDate: { en: 'Due date', ar: 'تاريخ الاستحقاق' },
  due_date: { en: 'Due date', ar: 'تاريخ الاستحقاق' },
  date: { en: 'Date', ar: 'التاريخ' },
  number: { en: 'Number', ar: 'الرقم' },
  reference: { en: 'Reference', ar: 'المرجع' },
  currency: { en: 'Currency', ar: 'العملة' },
  notes: { en: 'Notes', ar: 'الملاحظات' },
};

function humanize(value: string) {
  return value
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .replace(/\b\w/g, character => character.toUpperCase());
}

export function activityFieldLabel(field: string, locale: ActivityLocale = 'en') {
  return FIELD_LABELS[field]?.[locale] ?? humanize(field);
}

export function formatActivityValue(value: unknown, locale: ActivityLocale = 'en'): string | undefined {
  const copy = COPY[locale];
  if (value === null || value === undefined || value === '') return copy.none;
  if (typeof value === 'boolean') return value ? copy.yes : copy.no;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'bigint') return String(value);
  if (value instanceof Date) return value.toLocaleString(locale === 'ar' ? 'ar' : 'en');
  if (Array.isArray(value)) {
    const scalarValues = value.filter(item => ['string', 'number', 'boolean'].includes(typeof item));
    return scalarValues.length === value.length && value.length <= 6 ? scalarValues.join(', ') : undefined;
  }
  return undefined;
}

export function normalizeActivityAction(action: unknown, metadata: Record<string, unknown> = {}) {
  const normalized = String(action ?? 'ACTION').trim().toUpperCase().replace(/[\s-]+/g, '_');
  const metadataType = String(metadata.type ?? '').toUpperCase();
  const before = asRecord(metadata.before);
  const after = asRecord(metadata.after);
  const fromStatus = String(metadata.from ?? before.status ?? '').trim().toLowerCase();
  const toStatus = String(metadata.to ?? after.status ?? '').trim().toLowerCase();
  const approvalStatuses = new Set(['approved', 'accepted', 'awaiting payment', 'paid']);
  const removalStatuses = new Set(['void', 'deleted', 'cancelled', 'canceled']);

  if (metadataType === 'PAYMENT_REVERSED' || /PAYMENT_(REVERSED|REMOV)|REVERSE_PAYMENT|REVERSED_PAYMENT/.test(normalized)) return 'PAYMENT_REVERSED';
  if (metadataType === 'PAYMENT_RECEIVED' || normalized.includes('PAYMENT') || normalized === 'ADDED_PAYMENT') return 'PAYMENT';
  if (['CREATE', 'APPROVE', 'EDIT', 'REMOVE', 'ADD'].includes(normalized)) return normalized;
  if (['SCREENSHOT', 'TAB_SWITCH'].includes(normalized)) return normalized;
  if (approvalStatuses.has(toStatus) && !approvalStatuses.has(fromStatus)) return 'APPROVE';
  if (removalStatuses.has(toStatus) && !removalStatuses.has(fromStatus)) return 'REMOVE';
  if (/APPROV|ACCEPT/.test(normalized)) return 'APPROVE';
  if (/CREAT|GENERAT|IMPORT/.test(normalized) || normalized.startsWith('COPIED_')) return 'CREATE';
  if (/DELET|REMOV|VOID|CANCEL|RETURN/.test(normalized)) return 'REMOVE';
  if (/ATTACH|UPLOAD|SHARE|RECEIV/.test(normalized)) return 'ADD';
  if (/EDIT|UPDAT|STATUS_CHANGE/.test(normalized)) return 'EDIT';
  return normalized;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {};
}

export function translateActivityEvent(input: ActivityEventInput, locale: ActivityLocale = 'en'): TranslatedActivityEvent {
  const copy = COPY[locale];
  const metadata = asRecord(input.metadata);
  const action = normalizeActivityAction(input.action, metadata);
  const target = String(input.targetName ?? input.module ?? '').trim();
  const targetLabel = target || copy.document;
  const knownActions: Record<string, { label: string; category: TranslatedActivityEvent['category'] }> = {
    CREATE: { label: copy.create, category: 'create' },
    APPROVE: { label: copy.approve, category: 'approve' },
    PAYMENT: { label: copy.payment, category: 'payment' },
    PAYMENT_REVERSED: { label: copy.paymentReversed, category: 'payment' },
    EDIT: { label: copy.edit, category: 'edit' },
    REMOVE: { label: copy.remove, category: 'remove' },
    ADD: { label: copy.add, category: 'add' },
    SCREENSHOT: { label: copy.security, category: 'security' },
    TAB_SWITCH: { label: copy.security, category: 'security' },
  };
  const definition = knownActions[action];
  const isKnown = Boolean(definition);
  const actionLabel = definition?.label ?? copy.activity;
  const before = asRecord(metadata.before);
  const after = asRecord(metadata.after);
  const explicitFields = Array.isArray(metadata.changedFields)
    ? metadata.changedFields.filter((field): field is string => typeof field === 'string')
    : [];
  const snapshotFields = Array.from(new Set([...Object.keys(before), ...Object.keys(after)]));
  const fields = (explicitFields.length ? explicitFields : snapshotFields).filter(field => !HIDDEN_FIELDS.has(field));
  const changes = fields.flatMap(field => {
    const oldValue = formatActivityValue(before[field], locale);
    const newValue = formatActivityValue(after[field], locale);
    if (oldValue === undefined && newValue === undefined) return [];
    return [{ field, label: activityFieldLabel(field, locale), before: oldValue, after: newValue }];
  });

  const factDefinitions: Array<[string, string, unknown]> = [
    ['amount', copy.amount, metadata.amount],
    ['account', copy.account, metadata.account],
    ['reference', copy.reference, metadata.reference],
    ['method', copy.method, metadata.paymentMethod ?? metadata.method],
    ['paymentDate', copy.paymentDate, metadata.paymentDate ?? metadata.date],
    ['paymentType', copy.paymentType, metadata.paymentType],
    ['contact', copy.contact, metadata.afterContact ?? metadata.beforeContact],
  ];
  const facts = factDefinitions.flatMap(([, label, value]) => {
    const formatted = formatActivityValue(value, locale);
    return value === undefined || formatted === undefined ? [] : [{ label, value: formatted }];
  });
  const readableDetail = [metadata.details, metadata.reason].find(value => typeof value === 'string' && value.trim()) as string | undefined;
  const statusChange = changes.find(change => change.field.toLowerCase() === 'status');
  const summary = readableDetail
    ?? (statusChange ? `${copy.changed} ${copy.status} ${copy.from} ${statusChange.before ?? copy.none} ${copy.to} ${statusChange.after ?? copy.none}` : undefined);

  return {
    action,
    actionLabel,
    title: isKnown ? `${actionLabel}: ${targetLabel}` : copy.activity,
    summary,
    category: definition?.category ?? 'unknown',
    changes,
    facts,
    isKnown,
  };
}
